/**
 * Parallax mountain/iceberg silhouettes at the horizon.
 *
 * Two layers of jagged polygons drawn with cool blue gradients.
 * Back layer (farther, paler, slower parallax)
 * Front layer (closer, darker, faster parallax)
 *
 * Parallax is driven by mouse X offset passed into tick().
 */

interface Range {
  peaks: number[]       // normalized heights 0..1 across width
  baseY: number         // y position of the base in pixels
  peakHeight: number    // max pixel height of the tallest peak
  topColor: string
  baseColor: string
  parallaxAmp: number   // px shift range driven by mouse
}

export class Mountains {
  private w = 0
  private h = 0
  private back: Range | null = null
  private front: Range | null = null
  private mouseXNorm = 0 // -1..1

  resize(w: number, h: number): void {
    this.w = w
    this.h = h

    // Back range — paler, taller peaks, sits higher
    this.back = {
      peaks: generatePeaks(12, 0.3, 1.0, 13),
      baseY: h * 0.72,
      peakHeight: h * 0.32,
      topColor: 'rgba(194, 224, 248, 0.85)',
      baseColor: 'rgba(140, 188, 228, 0.55)',
      parallaxAmp: 24,
    }

    // Front range — closer, darker, jagged
    this.front = {
      peaks: generatePeaks(10, 0.2, 0.85, 7),
      baseY: h * 0.86,
      peakHeight: h * 0.22,
      topColor: 'rgba(120, 170, 210, 0.9)',
      baseColor: 'rgba(60, 110, 170, 0.7)',
      parallaxAmp: 48,
    }
  }

  setMouseXNorm(xNorm: number): void {
    // clamp -1..1
    this.mouseXNorm = Math.max(-1, Math.min(1, xNorm))
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (!this.back || !this.front) return
    this.drawRange(ctx, this.back, this.mouseXNorm * this.back.parallaxAmp)
    this.drawRange(ctx, this.front, this.mouseXNorm * this.front.parallaxAmp)
  }

  private drawRange(ctx: CanvasRenderingContext2D, r: Range, shift: number): void {
    const g = ctx.createLinearGradient(0, r.baseY - r.peakHeight, 0, r.baseY)
    g.addColorStop(0, r.topColor)
    g.addColorStop(1, r.baseColor)
    ctx.fillStyle = g

    ctx.beginPath()
    const count = r.peaks.length
    const step = this.w / (count - 1)

    ctx.moveTo(-40 + shift, r.baseY)
    for (let i = 0; i < count; i++) {
      const x = i * step + shift
      const y = r.baseY - r.peakHeight * r.peaks[i]
      if (i === 0) {
        ctx.lineTo(x, y)
      } else {
        // Sharp peaks — direct lineTo
        ctx.lineTo(x, y)
      }
    }
    ctx.lineTo(this.w + 40 + shift, r.baseY)
    ctx.lineTo(this.w + 40 + shift, this.h + 20)
    ctx.lineTo(-40 + shift, this.h + 20)
    ctx.closePath()
    ctx.fill()

    // Crisp snow-line highlight on the top edge
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)'
    ctx.lineWidth = 1.2
    ctx.beginPath()
    for (let i = 0; i < count; i++) {
      const x = i * step + shift
      const y = r.baseY - r.peakHeight * r.peaks[i]
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.stroke()
  }
}

/**
 * Procedural jagged peak heights.
 * count: number of peaks
 * min/max: height range 0..1
 * seed: deterministic offset for variety
 */
function generatePeaks(count: number, min: number, max: number, seed: number): number[] {
  const peaks: number[] = []
  // Use seeded pseudo-random
  let s = seed
  const rand = () => {
    s = (s * 9301 + 49297) % 233280
    return s / 233280
  }
  for (let i = 0; i < count; i++) {
    const v = min + rand() * (max - min)
    peaks.push(v)
  }
  return peaks
}
